import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { Badge, Button, Modal, Popover } from "../../../shared";
import useOutsideClick from "../../../hooks/useOutsideClick";
import {
  useAddTagMutation,
  useGetTagsQuery,
} from "../../../redux/services/tags/tagsApi";
import { useUpdateAdminCustomerMutation } from "../../../redux/services/admin/adminApi";
import { TagBadgeList, TagsPickerMenu } from "../../../components/Tags";

const DEFAULT_COLOR = "#64748b";

function normalizeTagId(value) {
  if (value == null || value === "") return "";
  const valueType = typeof value;
  if (valueType === "string" || valueType === "number") return String(value);
  if (valueType === "object") {
    if (Array.isArray(value)) return "";
    if (value.$oid != null) return String(value.$oid);
    if (value._id != null) return normalizeTagId(value._id);
    if (value.id != null) return normalizeTagId(value.id);
    if (typeof value.toString === "function") {
      try {
        const stringValue = value.toString();
        if (/^[a-f\d]{24}$/i.test(stringValue)) return stringValue;
      } catch (error) {
        return "";
      }
    }
  }
  return "";
}

const normalizeSelectedTagIds = (tags = []) =>
  Array.isArray(tags)
    ? tags.map((item) => normalizeTagId(item)).filter(Boolean)
    : [];

function buildTagMap(tags = []) {
  const map = new Map();
  tags.forEach((tag) => {
    const id = normalizeTagId(tag);
    if (id && tag && typeof tag === "object") map.set(id, tag);
  });
  return map;
}

function resolveTagsForDisplay(selectedIds, libraryTags, customerTags) {
  const libraryMap = buildTagMap(libraryTags);
  const populatedMap = buildTagMap(customerTags);
  return selectedIds
    .map((id) => libraryMap.get(id) || populatedMap.get(id))
    .filter((tag) => tag && typeof tag === "object");
}

export const CustomerTagsInline = ({
  customer = {},
  editable = true,
  onChangeTags,
  badgeSize = "sm",
  summarizeOverflow = false,
}) => {
  const [open, setOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tagToRemove, setTagToRemove] = useState(null);
  const [removingTag, setRemovingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_COLOR);
  const ref = useRef(null);
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [
      offset(8),
      flip({ allowedPlacements: ["bottom-start", "bottom-end", "top-start", "top-end"] }),
      shift({ padding: 8 }),
    ],
  });
  const { data, isLoading } = useGetTagsQuery({
    appliesTo: "customers",
    limit: 200,
  });
  const [addTag, { isLoading: creating }] = useAddTagMutation();
  const [updateCustomer, { isLoading: saving }] = useUpdateAdminCustomerMutation();
  const tags = data?.tags || [];
  const selectedIds = normalizeSelectedTagIds(customer.tags);

  useOutsideClick(ref, () => setOpen(false));

  const selectedTags = useMemo(
    () => resolveTagsForDisplay(selectedIds, tags, customer.tags),
    [customer.tags, selectedIds, tags]
  );

  const { visibleTags, overflowTags } = useMemo(() => {
    if (!summarizeOverflow) {
      return { visibleTags: selectedTags, overflowTags: [] };
    }

    return {
      visibleTags: selectedTags.slice(0, 1),
      overflowTags: selectedTags.slice(1),
    };
  }, [selectedTags, summarizeOverflow]);

  useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
    }
  }, [open]);

  async function saveTags(nextTagIds) {
    if (!customer?._id) return;

    if (typeof onChangeTags === "function") {
      await onChangeTags(nextTagIds);
      return;
    }

    await updateCustomer({
      id: customer._id,
      data: {
        tags: nextTagIds,
      },
    }).unwrap();
  }

  async function toggleTag(tag) {
    const tagId = normalizeTagId(tag);
    const nextTagIds = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : Array.from(new Set([...selectedIds, tagId]));

    try {
      await saveTags(nextTagIds);
    } catch (error) {
      toast(error?.data?.message || "Failed to update customer tags", {
        type: "error",
        toastId: "customer-tags-update-error",
      });
    }
  }

  async function removeTagFromCustomer() {
    if (!tagToRemove) return;
    const tagId = normalizeTagId(tagToRemove);
    const nextTagIds = selectedIds.filter((id) => id !== tagId);

    try {
      setRemovingTag(true);
      await saveTags(nextTagIds);
      setTagToRemove(null);
    } catch (error) {
      toast(error?.data?.message || "Failed to remove customer tag", {
        type: "error",
        toastId: "customer-tags-remove-error",
      });
    } finally {
      setRemovingTag(false);
    }
  }

  async function createAndAssignTag() {
    const name = newTagName.trim();
    if (!name) {
      toast("Tag name is required", {
        type: "error",
        toastId: "customer-inline-tag-name-required",
      });
      return;
    }

    try {
      const response = await addTag({
        name,
        color: newTagColor || DEFAULT_COLOR,
        appliesTo: ["customers"],
      }).unwrap();
      const createdTagId = normalizeTagId(response?.tag);
      if (createdTagId) {
        await saveTags(Array.from(new Set([...selectedIds, createdTagId])));
      }
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
      setShowCreateForm(false);
    } catch (error) {
      toast(error?.data?.message || "Failed to create customer tag", {
        type: "error",
        toastId: "customer-inline-tag-create-error",
      });
    }
  }

  return (
    <div className="relative ml-2 flex min-w-0 items-center gap-1.5" ref={ref}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <TagBadgeList
          tags={visibleTags}
          emptyText=""
          size={badgeSize}
          onRemoveTag={editable ? setTagToRemove : undefined}
        />
        {overflowTags.length > 0 && (
          <Popover
            position="top"
            offset={[-12, 0]}
            trigger={
              <Badge
                color="gray"
                label={`+${overflowTags.length}`}
                className="shrink-0 cursor-pointer hover:bg-gray-200"
              />
            }
          >
            <div className="space-y-1 p-2">
              {overflowTags.map((tag) => (
                <div
                  key={normalizeTagId(tag)}
                  className="whitespace-nowrap text-sm text-gray-800"
                >
                  {tag.name || tag.slug || "Tag"}
                </div>
              ))}
            </div>
          </Popover>
        )}
      </div>
      {editable && (
        <button
          ref={refs.setReference}
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 bg-white text-sm font-semibold leading-none text-gray-500 hover:border-primary-300 hover:text-primary-700"
          aria-label="Add customer tag"
          disabled={saving || creating}
          onClick={() => setOpen((prev) => !prev)}
        >
          +
        </button>
      )}

      {open && (
        <TagsPickerMenu
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            maxWidth: "calc(100vw - 1rem)",
          }}
          className="z-[9999]"
          label="Customer"
          isLoading={isLoading}
          tags={tags}
          selectedIds={selectedIds}
          checkboxIdPrefix="inline-customer-tag"
          saving={saving}
          creating={creating}
          showCreateForm={showCreateForm}
          onToggleCreateForm={() => setShowCreateForm((prev) => !prev)}
          onToggleTag={toggleTag}
          newTagName={newTagName}
          onNewTagNameChange={setNewTagName}
          newTagColor={newTagColor}
          onNewTagColorChange={setNewTagColor}
          onCreateAndAssign={createAndAssignTag}
          createPlaceholder="New customer tag"
        />
      )}

      <Modal
        visible={!!tagToRemove}
        setVisible={(visible) => {
          if (!visible) setTagToRemove(null);
        }}
        title="Remove Tag"
        size="sm"
        content={
          <div className="text-sm text-gray-700">
            Remove{" "}
            <span className="font-semibold text-gray-900">
              {tagToRemove?.name || "this tag"}
            </span>{" "}
            from this customer?
          </div>
        }
        footer={
          <>
            <Button type="alternative" onClick={() => setTagToRemove(null)}>
              Cancel
            </Button>
            <Button
              type="danger"
              loading={removingTag || saving}
              disabled={removingTag || saving}
              onClick={removeTagFromCustomer}
            >
              Remove
            </Button>
          </>
        }
      />
    </div>
  );
};

export default CustomerTagsInline;
